import {useState, useCallback, useRef} from 'react'
import config from '../config'

export const useHttp = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const isRefreshingRef = useRef(false)
  const failedQueueRef = useRef([])

  // Helper: Process queued requests after token refresh
  const processQueue = useCallback((error, token = null) => {
    failedQueueRef.current.forEach(prom => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueueRef.current = []
  }, [])

  // Helper: Refresh access token
  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await fetch(`${config.API_URL}api/refresh-token`, {
        method: 'POST',
        credentials: 'include'
      })
      
      if (!response.ok) {
        throw new Error('Token refresh failed')
      }
      
      return true
    } catch (err) {
      console.error('Token refresh error:', err)
      throw err
    }
  }, [])

  const request = useCallback(async (url, method = 'GET', body = null, headers = {}, retryCount = 0) => {
    setLoading(true)
    try {
      if (body) {
        body = JSON.stringify(body)
        headers['Content-Type'] = 'application/json'
      }
      // console.log('url:', url, '\nmethod:', method, '\nbody:', body, '\nheaders:', headers)

      const response = await fetch(url, {
        method, 
        body, 
        headers,
        credentials: 'include' // Include cookies in requests
      })
      
      // Handle 401 Unauthorized
      if (response.status === 401 && retryCount === 0) {
        const data = await response.json()
        
        // Don't try to refresh for login/logout endpoints
        if (url.includes('/api/login') || url.includes('/api/logout')) {
          const err = new Error(data.message || 'Authentication failed')
          err.data = data
          err.status = response.status
          setLoading(false)
          setError(err.message)
          throw err
        }
        
        // Token expired or missing, try to refresh
        if (isRefreshingRef.current) {
          // Already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueueRef.current.push({
              resolve: () => {
                // Retry original request after refresh
                request(url, method, body ? JSON.parse(body) : null, headers, retryCount + 1)
                  .then(resolve)
                  .catch(reject)
              },
              reject
            })
          })
        }

        isRefreshingRef.current = true

        try {
          // Attempt to refresh token
          await refreshAccessToken()
          isRefreshingRef.current = false
          processQueue(null, true)
          
          // Retry original request with new token
          return await request(url, method, body ? JSON.parse(body) : null, headers, retryCount + 1)
        } catch (refreshError) {
          isRefreshingRef.current = false
          processQueue(refreshError, null)
          
          // Refresh failed, clear auth and redirect to login
          console.error('Authentication failed, redirecting to login')
          localStorage.removeItem('userData')
          window.location.href = '/'
          throw new Error('Session expired, please login again')
        }
      }
      
      const data = await response.json()

      if (!response.ok) {
        const err = new Error(data.message || 'Something wrong')
        err.data = data
        err.status = response.status
        throw err
      }

      setLoading(false)

      return data
    } catch (e) {
      setLoading(false)
      setError(e.message)
      throw e
    }
  }, [refreshAccessToken, processQueue])

  const clearError = useCallback(() => setError(null), [])

  return { loading, request, error, clearError }
}
